import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AspectRatio } from '@src/types.ts/video';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import { FIT_DEFAULT } from '../../config/videoSettings';
import { numericalInputStyles } from '../../styles/mainStyle';
import PanelSliderField from '../common/PanelSliderField';
import { ElementAnimationSettings } from './ElementAnimationSettings';
import { GenericAnimationSettings } from './GenericAnimationSettings';
import PanelSection, { FieldRow } from './PanelSection';
import { PropertyCaption } from './PropertyCaption';
import { PropertyNumericalInput } from './PropertyNumericalInput';
import useLayoutAwareVideoSettingsState from './useLayoutAwareVideoSettingsState';
import { VideoFitPropertySelect } from './VideoFitPropertySelect';
import { VideoPreset } from './VideoPreset';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface VideoSettingsProps {
  activeElement: ElementState;
  editorStore: EditorStoreLike;
}

export const VideoSettings: React.FC<VideoSettingsProps> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const {
    enableStackedLayouts,
    creatorStudioTimelineEnableFit13,
    creatorStudioTimelineEnableFitSplitHorizontal11,
    creatorStudioTimelineEnableFitSplitHorizontal13,
    creatorStudioSettingsAnimationAndAudioFadeOnly,
  } = useFlagsCombination(userIdentity.currentRole);

  const {
    activeTimelineVideoElement, // layout child's hidden parent or regular active
    displayElement, // layout child or regular active
    isLayoutComposition,
    sourceUrl,
    xValue,
    yValue,
    modifyPropertyWithSlider,
    handleChangeScale,
    updateTimelineVideoProperty,
  } = useLayoutAwareVideoSettingsState({
    activeElement: props.activeElement,
    editorStore: props.editorStore,
  });

  if (!props.activeElement) {
    return null;
  }

  const isBroll = !videoCreator.isOriginalVideoElement(
    activeTimelineVideoElement.source,
  );

  const is916 = videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_9_16;

  // When this flag is on, the panel is reduced to Enter Animation (+ length)
  // and Audio Fade In/Out only — every other control is hidden.
  const showFullSettings = !creatorStudioSettingsAnimationAndAudioFadeOnly;

  // For the original video element (not b-roll), alignment sliders only apply
  // to vertical (9:16) scenes and live in a "Current Scene only" section gated
  // behind showFullSettings; scale/zoom is hidden entirely.
  const showAlignmentSliders = showFullSettings && (isBroll || is916);
  const showZoom = showFullSettings && isBroll;

  const zoomValue = (() => {
    const width = displayElement?.source?.width;
    if (width === undefined) return 100;
    const widthVal = parseFloat(width.toString());
    return !isNaN(widthVal) ? widthVal : 100;
  })();

  const startDisabledReason =
    "The original video's timing follows the story's edit";

  return (
    <VideoSettingsContainer>
      {showFullSettings && isBroll && (
        <VideoPresetContainer>
          <VideoPreset activeElement={displayElement} url={sourceUrl} />
        </VideoPresetContainer>
      )}

      {showFullSettings && (
        <PanelSection title="Timing">
          <FieldRow>
            <div>
              <PropertyCaption>Start</PropertyCaption>
              <PropertyNumericalInput
                activeElement={activeTimelineVideoElement}
                propertyName="time"
                defaultValue="auto"
                unit="s"
                disabled={!isBroll}
                disabledReason={startDisabledReason}
                customStyles={numericalInputStyles}
                getInputValue={async (property: string, value: string) => {
                  await updateTimelineVideoProperty({ [property]: value });
                }}
              />
            </div>
            <div>
              <PropertyCaption>Duration</PropertyCaption>
              <PropertyNumericalInput
                activeElement={activeTimelineVideoElement}
                propertyName="duration"
                defaultValue="auto"
                unit="s"
                disabled={!isBroll}
                disabledReason={startDisabledReason}
                customStyles={numericalInputStyles}
                getInputValue={async (property: string, value: string) => {
                  await updateTimelineVideoProperty({ [property]: value });
                }}
              />
            </div>
          </FieldRow>
        </PanelSection>
      )}

      {showFullSettings && isBroll && (
        <PanelSection title="Layout">
          <PropertyCaption>
            {isLayoutComposition ? 'Layout Type' : 'Fit'}
          </PropertyCaption>
          <VideoFitPropertySelect
            activeElement={activeTimelineVideoElement}
            propertyName="fit"
            defaultValue={isLayoutComposition ? 'stacked-1-1' : FIT_DEFAULT}
            options={[
              { caption: 'Cover', value: 'cover' },
              { caption: 'Contain', value: 'contain' },
              ...(enableStackedLayouts &&
              videoCreator.currentVideo?.aspectRatio === AspectRatio.AR_9_16
                ? [
                    ...(creatorStudioTimelineEnableFitSplitHorizontal11
                      ? [
                          {
                            caption: 'Stacked 1:1',
                            value: 'stacked-1-1',
                          },
                        ]
                      : []),
                    ...(creatorStudioTimelineEnableFit13 &&
                    creatorStudioTimelineEnableFitSplitHorizontal13
                      ? [
                          {
                            caption: 'Stacked 1:3',
                            value: 'stacked-1-3',
                          },
                        ]
                      : []),
                  ]
                : []),
            ]}
          />
        </PanelSection>
      )}

      <PanelSection title="Audio">
        {showFullSettings && isBroll && (
          <FieldRow>
            <div>
              <PropertyCaption>Volume</PropertyCaption>
              <PropertyNumericalInput
                activeElement={activeTimelineVideoElement}
                propertyName="volume"
                defaultValue="100"
                unit="%"
                customStyles={numericalInputStyles}
                getInputValue={async (property: string, value: string) => {
                  await updateTimelineVideoProperty({ [property]: value });
                }}
              />
            </div>
          </FieldRow>
        )}
        <FieldRow>
          <div>
            <PropertyCaption>Audio Fade In</PropertyCaption>
            <PropertyNumericalInput
              activeElement={activeTimelineVideoElement}
              propertyName="audio_fade_in"
              defaultValue="0"
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateTimelineVideoProperty({
                  [property]: parseFloat(value),
                });
              }}
            />
          </div>
          <div>
            <PropertyCaption>Audio Fade Out</PropertyCaption>
            <PropertyNumericalInput
              activeElement={activeTimelineVideoElement}
              propertyName="audio_fade_out"
              defaultValue="0"
              unit="s"
              customStyles={numericalInputStyles}
              getInputValue={async (property: string, value: string) => {
                await updateTimelineVideoProperty({
                  [property]: parseFloat(value),
                });
              }}
            />
          </div>
        </FieldRow>
      </PanelSection>

      {showAlignmentSliders && (
        <PanelSection title="Position">
          {!isBroll && <SectionHint>Current scene only</SectionHint>}
          <PanelSliderField
            label="Horizontal Alignment"
            value={xValue}
            min={0}
            max={100}
            snapTo={[0, 50, 100]}
            onChange={newValue => modifyPropertyWithSlider('x', newValue)}
          />
          <PanelSliderField
            label="Vertical Alignment"
            value={yValue}
            min={0}
            max={100}
            snapTo={[0, 50, 100]}
            onChange={newValue => modifyPropertyWithSlider('y', newValue)}
          />
          {showZoom && (
            <PanelSliderField
              label="Zoom"
              value={zoomValue}
              min={20}
              max={500}
              midpoint={100}
              snapTo={[100]}
              onChange={newValue => handleChangeScale(newValue)}
            />
          )}
        </PanelSection>
      )}

      <PanelSection title="Animation">
        {showFullSettings && isBroll && (
          <ElementAnimationSettings
            activeElement={activeTimelineVideoElement}
            editorStore={props.editorStore}
          />
        )}
        <GenericAnimationSettings
          hideFadeAnimations
          activeElement={activeTimelineVideoElement}
          editorStore={props.editorStore}
        />
      </PanelSection>
    </VideoSettingsContainer>
  );
});

const VideoSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  padding-bottom: 20px;
  box-sizing: border-box;
`;

const VideoPresetContainer = styled.div`
  margin: var(--spacing-md) 0 var(--spacing-lg);
`;

/** A one-line note under a section title (field-level hint style). */
const SectionHint = styled.p`
  margin: 4px 0 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;
